'use client';

import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { DynamicSvgShapeHost } from './DynamicSvgShapeHost';
import { parseSvgShapeSettingsJson } from './applyDynamicSvgStyles';
import {
  PlaceholderChromeOverlay,
  placeholderDashedBorderCss,
  PLACEHOLDER_UNLINKED_OPACITY,
} from './PlaceholderChrome';
import {
  parseLinkTarget,
  tryInterceptExternalLinkClick,
  shouldConfirmExternalNavigation,
  isPrimaryUnmodifiedClick,
} from './linkOutParse';
import { ItemHrefControl } from './ItemHrefControl';
import {
  resolveDefaultTitleStyle,
  resolveTitleTextDecorationLine,
  thumbnailFitToObjectFit,
} from './defaultTitleStyle';
import {
  dropShadowToBoxShadow,
  resolveDefaultDropShadow,
} from './defaultDropShadow';
import { resolveTemplateFontCssFamily } from '../../vendor/template-system/v2/types/templateFonts';

export interface GenericItemHostProps {
  width: number;
  height: number;
  title: string;
  borderRadius?: number;
  frameThickness?: number;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  fontSize?: number;
  imageUrl?: string | null;
  /** Serialized Containd svgShapeSettings JSON. */
  svgShapeSettings?: string | null;
  /** Sparse Containd `defaultTitleStyle` JSON (generic / renderAsBuiltin pill). */
  defaultTitleStyle?: string | null;
  /** Sparse Containd `defaultDropShadow` JSON for the item shell. */
  defaultDropShadow?: string | null;
  /** Fallback when drop-shadow JSON omits color (share theme `itemDropShadow`). */
  dropShadowFallbackColor?: string | null;
  childrenCount?: number;
  showItemLabels?: boolean;
  showItemChildrenCount?: boolean;
  /** DEC-062 placeholder seat chrome. */
  isPlaceholder?: boolean;
  /** False when the original is not in this freeze (muted chrome, no jump). */
  placeholderLinked?: boolean;
  /** Outbound title link (URL / mailto / tel / item:<uuid>). */
  linkOut?: string | null;
  /** Navigate to an in-share item uuid (item: linkOut). */
  onItemLinkNavigate?: (itemUuid: string) => void;
  /**
   * External / item: linkOut open target. Default true (`_blank`).
   * When true and `itemLinkHref` is set, `item:` is a real `<a target=_blank>`.
   */
  openLinksInNewTab?: boolean;
  /** Resolve an in-share `item:` uuid to a share path. */
  itemLinkHref?: (itemUuid: string) => string | null | undefined;
  /**
   * When set, outbound http(s)/unknown-scheme clicks open a leave-site confirm
   * instead of navigating immediately (Spaces freeze viewer).
   */
  onExternalLinkOpen?: (href: string) => void;
  onClick?: () => void;
  overlay?: ReactNode;
}

function withAlpha(hex: string, alpha: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const r = parseInt(m[1]!, 16);
  const g = parseInt(m[2]!, 16);
  const b = parseInt(m[3]!, 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * HTML twin of Containd `ItemCanvasElement` (generic / renderAsBuiltin surface).
 * Shell + optional full-bleed thumb (incl. dynamic SVG shape) + title pill.
 */
export function GenericItemHost({
  width,
  height,
  title,
  borderRadius = 8,
  frameThickness = 2,
  backgroundColor = '#2d2d2d',
  borderColor = '#555555',
  textColor = '#ffffff',
  fontSize = 14,
  imageUrl,
  svgShapeSettings,
  defaultTitleStyle,
  defaultDropShadow,
  dropShadowFallbackColor,
  childrenCount = 0,
  showItemLabels = true,
  showItemChildrenCount = true,
  isPlaceholder = false,
  placeholderLinked = true,
  linkOut = null,
  onItemLinkNavigate,
  openLinksInNewTab = true,
  itemLinkHref,
  onExternalLinkOpen,
  onClick,
  overlay,
}: GenericItemHostProps) {
  const shape = parseSvgShapeSettingsJson(svgShapeSettings);
  const hideBg = shape?.hideSquareBackground === true;
  const hideFrame = shape?.hideSquareFrame === true;
  const effectiveFrame = hideFrame ? 0 : frameThickness;
  const effectiveBg = hideBg ? 'transparent' : backgroundColor;
  const effectiveBorder = hideFrame ? 'transparent' : borderColor;
  const useDynamicSvg = Boolean(imageUrl && shape?.isDynamicSVG);
  const dashed = isPlaceholder
    ? placeholderDashedBorderCss(
        effectiveFrame,
        effectiveBorder,
        placeholderLinked,
      )
    : null;

  const shell: CSSProperties = {
    width,
    height,
    borderRadius,
    borderWidth: dashed?.borderWidth ?? effectiveFrame,
    borderStyle: dashed?.borderStyle ?? 'solid',
    borderColor: dashed?.borderColor ?? effectiveBorder,
    backgroundColor: effectiveBg,
    boxSizing: 'border-box',
    overflow: 'hidden',
    position: 'relative',
    cursor: onClick ? 'pointer' : 'default',
    padding: imageUrl ? 0 : 8,
    opacity:
      isPlaceholder && !placeholderLinked
        ? PLACEHOLDER_UNLINKED_OPACITY
        : undefined,
  };

  const titleStyle = resolveDefaultTitleStyle(defaultTitleStyle, {
    hasThumbnail: Boolean(imageUrl),
  });
  const dropShadow = resolveDefaultDropShadow(defaultDropShadow, {
    fallbackColor: dropShadowFallbackColor ?? undefined,
  });
  const boxShadow = dropShadowToBoxShadow(dropShadow);
  const showTitlePill = showItemLabels && titleStyle.textboxOpacity > 0;
  const showChildren =
    (titleStyle.showChildrenCount !== undefined
      ? titleStyle.showChildrenCount
      : showItemChildrenCount) && childrenCount > 0;
  const maxTitleWidth = Math.max(48, width - (Number(shell.borderWidth) || 0) * 2 - 8);
  const titleLink = parseLinkTarget(linkOut ?? undefined);
  const [imgSrc, setImgSrc] = useState(imageUrl ?? '');
  useEffect(() => {
    setImgSrc(imageUrl ?? '');
  }, [imageUrl]);

  const overlayJustifyContent =
    titleStyle.verticalAlign === 'top'
      ? 'flex-start'
      : titleStyle.verticalAlign === 'bottom'
        ? 'flex-end'
        : 'center';
  const overlayAlignItems =
    titleStyle.horizontalAlign === 'left'
      ? 'flex-start'
      : titleStyle.horizontalAlign === 'right'
        ? 'flex-end'
        : 'center';
  const textDecorationLine = resolveTitleTextDecorationLine(
    titleStyle.underlined,
    titleStyle.strikethrough,
    titleStyle.underlineStrokeWidth == null,
  );
  const titleLineHeightPx = fontSize * titleStyle.lineHeight;
  const titleTextStyle: CSSProperties = {
    position: 'relative',
    zIndex: 1,
    color: textColor,
    fontSize,
    fontFamily: `${resolveTemplateFontCssFamily(undefined)}, 'Source Sans 3', system-ui, sans-serif`,
    fontWeight: titleStyle.bold ? 600 : 400,
    fontStyle: titleStyle.italic ? 'italic' : 'normal',
    lineHeight: titleStyle.lineHeight,
    textAlign: titleStyle.horizontalAlign,
    overflow: 'hidden',
    maxHeight:
      titleStyle.maxLines != null
        ? titleLineHeightPx * titleStyle.maxLines
        : undefined,
    display: titleStyle.maxLines != null && titleStyle.maxLines > 1 ? '-webkit-box' : undefined,
    WebkitLineClamp: titleStyle.maxLines ?? undefined,
    WebkitBoxOrient:
      titleStyle.maxLines != null && titleStyle.maxLines > 1 ? 'vertical' : undefined,
    textDecoration: textDecorationLine === 'none' ? 'none' : textDecorationLine,
  };

  const childrenBadge = showChildren ? (
    <div
      style={{
        marginTop: showTitlePill ? 4 : 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: '#fff',
        fontSize: 12,
        fontWeight: 500,
        padding: '2px 6px',
        borderRadius: 4,
      }}
    >
      [{childrenCount}]
    </div>
  ) : null;

  const handleTitleLinkClick = (
    e: MouseEvent,
  ) => {
    if (!titleLink) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (titleLink.kind === 'external') {
      if (tryInterceptExternalLinkClick(e, titleLink.href, onExternalLinkOpen)) {
        return;
      }
      e.stopPropagation();
      // Confirm-gated href + modifier/middle click → native <a> navigation
      if (
        onExternalLinkOpen &&
        shouldConfirmExternalNavigation(titleLink.href) &&
        !isPrimaryUnmodifiedClick(e)
      ) {
        return;
      }
      e.preventDefault();
      if (openLinksInNewTab) {
        window.open(titleLink.href, '_blank', 'noopener,noreferrer');
      } else {
        window.location.assign(titleLink.href);
      }
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    onItemLinkNavigate?.(titleLink.ref);
  };

  return (
    <div
      style={{
        width,
        height,
        borderRadius,
        boxShadow,
        // Keep shadow outside the clipped shell.
        overflow: 'visible',
      }}
    >
    <div
      style={shell}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      data-generic-item
      data-svg-shape={shape?.isDynamicSVG ? '1' : undefined}
      data-placeholder={isPlaceholder ? '1' : undefined}
      data-placeholder-unlinked={
        isPlaceholder && !placeholderLinked ? '1' : undefined
      }
    >
      {imageUrl ? (
        useDynamicSvg ? (
          <div style={{ position: 'absolute', inset: 0 }}>
            <DynamicSvgShapeHost
              src={imageUrl}
              svgShapeSettings={svgShapeSettings}
              backgroundColor={backgroundColor}
              borderColor={borderColor}
              frameThickness={frameThickness}
              textColor={textColor}
              width={width}
              height={height}
              style={{
                borderRadius: Math.max(0, borderRadius - effectiveFrame),
              }}
            />
          </div>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imgSrc || imageUrl}
            alt=""
            loading="lazy"
            onError={() => {
              const current = (imgSrc || imageUrl || '').split('#')[0] ?? '';
              if (current.includes('_thumb')) {
                setImgSrc(current.replace(/_thumb(?=\?|$)/, ''));
              }
            }}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              // Static SVG files letterbox (SvgFileImage meet); raster photos honor
              // the frozen title-style thumbnailFit (cover default).
              objectFit: /\.svg(?:$|\?)/i.test(imageUrl)
                ? 'contain'
                : thumbnailFitToObjectFit(titleStyle.thumbnailFit),
              borderRadius: Math.max(0, borderRadius - effectiveFrame),
            }}
          />
        )
      ) : null}

      {showItemLabels ? (
        <div
          style={{
            position: 'absolute',
            top: effectiveFrame,
            left: effectiveFrame,
            right: effectiveFrame,
            bottom: effectiveFrame,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: overlayJustifyContent,
            alignItems: overlayAlignItems,
            paddingLeft: 4,
            paddingRight: 4,
            pointerEvents: 'none',
          }}
        >
          {showTitlePill ? (
            <div
              style={{
                position: 'relative',
                maxWidth: maxTitleWidth,
                paddingTop: titleStyle.paddingVertical,
                paddingBottom: titleStyle.paddingVertical,
                paddingLeft: titleStyle.paddingHorizontal,
                paddingRight: titleStyle.paddingHorizontal,
                borderRadius: 6,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: imageUrl ? 'rgba(0,0,0,0.16)' : 'transparent',
                opacity: titleStyle.textboxOpacity,
                pointerEvents: titleLink ? 'auto' : 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 6,
                  backgroundColor: imageUrl
                    ? withAlpha(backgroundColor, titleStyle.backdropOpacity)
                    : backgroundColor,
                  zIndex: 0,
                }}
              />
              <div style={{ position: 'relative', zIndex: 1 }}>
                {titleLink?.kind === 'external' ? (
                  <a
                    href={titleLink.href}
                    target={openLinksInNewTab ? '_blank' : '_self'}
                    rel={openLinksInNewTab ? 'noopener noreferrer' : undefined}
                    onClick={handleTitleLinkClick}
                    style={titleTextStyle}
                    data-title-link-out="1"
                  >
                    {title}
                  </a>
                ) : titleLink?.kind === 'item' ? (
                  <ItemHrefControl
                    itemRef={titleLink.ref}
                    paintContext={{
                      onItemLinkNavigate,
                      openLinksInNewTab,
                      itemLinkHref,
                      onExternalLinkOpen,
                    }}
                    dataTitleLinkOut
                    style={{
                      ...titleTextStyle,
                      width: '100%',
                    }}
                  >
                    {title}
                  </ItemHrefControl>
                ) : (
                  <div style={titleTextStyle}>{title}</div>
                )}
                {titleStyle.underlined && titleStyle.underlineStrokeWidth != null ? (
                  <div
                    style={{
                      marginTop: 2,
                      height: titleStyle.underlineStrokeWidth,
                      alignSelf: 'stretch',
                      backgroundColor: textColor,
                    }}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
          {childrenBadge}
        </div>
      ) : showChildren ? (
        <div
          style={{
            position: 'absolute',
            inset: effectiveFrame,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {childrenBadge}
        </div>
      ) : null}

      {isPlaceholder ? (
        <PlaceholderChromeOverlay linked={placeholderLinked} />
      ) : null}
      {overlay}
    </div>
    </div>
  );
}
